import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { Button, Input } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';
import { switchAccountType } from '@shared/auth/switchAccountType';

/**
 * Add the brands this business follows (2026-10-28: brands are tracked, not owned).
 * The user types an Instagram handle; onboarding_resolve_handle says what it is:
 *   • brand we know       → preview of what creators posted about it → «Добавить»
 *   • handle we don't know → /api/check-ig-handle asks Instagram first (a typo used to become
 *     a brand that never collects anything); Instagram says «does not exist» → say so and
 *     offer no button; found or unknown (check failed / throttled) → «Добавить»
 *   • creator             → offer the creator app instead
 * my_add_brand activates the account on the first brand (org, customer role, features),
 * so there is nothing to verify and nobody to wait for. Several organizations may follow
 * the same brand. Rendered on /customer/connect and in Profile.
 */

type Resolved =
  | { kind: 'brand'; brand_id: string | null; name: string; followers: number | null; handle: string; claim_state: 'claimable' | 'mine' }
  | { kind: 'creator'; handle: string }
  | { kind: 'unknown'; handle: string }
  | { kind: 'empty' };

type IgCheck =
  | { status: 'checking' }
  | { status: 'exists'; full_name: string | null; followers: number; is_private: boolean }
  | { status: 'not_found' }
  | { status: 'unchecked' };

interface BrandPreview {
  posts: number; stories: number; creators: number; reach: number; last_at: string | null;
  top_creators: { handle: string; followers: number }[];
}
const compactNum = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));

export default function OnboardBrand() {
  const { t } = useTranslation('customer');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  const { user, accessStatus } = useAuth();
  const navigate = useNavigate();
  const invalidate = useInvalidateSupabase();

  const [handle, setHandle] = useState('');
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  // Instagram check for a handle we have no data on.
  const [igCheck, setIgCheck] = useState<IgCheck | null>(null);

  // Brands this organization already follows.
  const { data: myBrands } = useSupabaseQuery<{ brand_id: string; name: string }[]>(
    ['get_my_brands', user?.id],
    async () => (user ? supabase.rpc('get_my_brands') : { data: [], error: null }),
    { enabled: !!user },
  );
  const connected = myBrands ?? [];

  // What creators already posted about the brand the user just found.
  const previewBrandId = resolved?.kind === 'brand' && resolved.claim_state === 'claimable' ? resolved.brand_id : null;
  const { data: preview } = useSupabaseQuery<BrandPreview | null>(
    ['my-brand-preview', previewBrandId],
    async () => (previewBrandId ? supabase.rpc('my_brand_preview', { p_brand: previewBrandId }) : { data: null, error: null }),
    { enabled: !!previewBrandId, staleTime: 5 * 60_000 },
  );

  // The first brand flips the account from preview to active: take the user to the thing
  // they came for. Only on that transition — Profile embeds this panel for active users.
  const prevStatus = useRef(accessStatus);
  useEffect(() => {
    if (prevStatus.current && prevStatus.current !== 'active' && accessStatus === 'active') {
      navigate('/customer/my-analytics', { replace: true });
    }
    prevStatus.current = accessStatus;
  }, [accessStatus, navigate]);

  const mapErr = (m: string): string =>
    m.includes('invalid_handle') ? tr('brandAdd.errInvalid', 'Проверьте Instagram: только латинские буквы, цифры, точки и подчёркивания.')
    : m.includes('handle_is_creator') ? tr('brandAdd.errCreator', 'Это профиль блогера, а не бренда.')
    : m.includes('creator_account') ? tr('brandAdd.errCreatorAccount', 'Вы вошли как блогер. Переключитесь в кабинет бизнеса, чтобы добавлять бренды.')
    : m.includes('too_many_brands') ? tr('brandAdd.errTooMany', 'Можно отслеживать до 20 брендов. Удалите лишние в профиле.')
    : m;

  const resolveHandle = async () => {
    setErr(null); setResolved(null); setAdded(null);
    if (handle.trim().length < 2) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('onboarding_resolve_handle', { p_handle: handle.trim() });
    setBusy(false);
    if (error) { setErr(mapErr(error.message)); return; }
    const r = data as Resolved;
    setResolved(r);
    setIgCheck(null);
    if (r.kind === 'unknown') void checkInstagram(r.handle);
  };

  const checkInstagram = async (h: string) => {
    setIgCheck({ status: 'checking' });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/check-ig-handle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token ?? ''}` },
        body: JSON.stringify({ handle: h }),
      });
      const j = await resp.json().catch(() => ({}));
      setIgCheck(j?.status === 'exists' || j?.status === 'not_found' ? j as IgCheck : { status: 'unchecked' });
    } catch {
      setIgCheck({ status: 'unchecked' });
    }
  };

  const addBrand = async (h: string) => {
    setErr(null); setBusy(true);
    const { data, error } = await supabase.rpc('my_add_brand', { p_handle: h });
    if (error) { setBusy(false); setErr(mapErr(error.message)); return; }
    // Features and the active status live in the JWT: refresh it so «Моя аналитика» and
    // jobs open now, not at the next token refresh. AuthContext picks up TOKEN_REFRESHED.
    try { await supabase.auth.refreshSession(); } catch { /* non-fatal */ }
    invalidate(['get_my_brands']); invalidate(['get_my_social_analytics']); invalidate(['my-brand-scope']);
    setBusy(false);
    setAdded((data as { name?: string } | null)?.name ?? h);
    setResolved(null); setIgCheck(null); setHandle('');
  };

  const removeBrand = async (brandId: string, name: string) => {
    if (!confirm(tr('brandAdd.removeConfirm', 'Перестать отслеживать «{{name}}»?', { name }))) return;
    setErr(null); setBusy(true);
    const { error } = await supabase.rpc('my_remove_brand', { p_brand: brandId });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    invalidate(['get_my_brands']); invalidate(['get_my_social_analytics']); invalidate(['my-brand-scope']);
  };

  // A creator who signed up as a business: flip the account type. The handle is stashed
  // so the creator claim screen starts with it.
  const becomeCreator = async (h: string) => {
    setErr(null); setBusy(true);
    try { localStorage.setItem('adamar_claim_handle', h); } catch { /* private mode */ }
    try { await switchAccountType('creator'); }
    catch (e) { setErr((e as Error)?.message ?? String(e)); setBusy(false); }
  };

  const box = (bg: string, border: string): React.CSSProperties => ({
    padding: 12, background: bg, border: `1px solid ${border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10,
  });

  return (
    <div className="c-panel" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* No own title: every host (Мой бренд, /connect, «Управлять брендами») already says what this is. */}
      <p style={{ fontSize: 'var(--fs-body)', color: 'var(--muted)', margin: 0 }}>
        {tr('brandAdd.multi', 'Можно добавить несколько брендов.')}
      </p>

      {connected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {connected.map((b) => (
            <span key={b.brand_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 6px 3px 10px', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.4)', borderRadius: 999, fontSize: 'var(--fs-small)' }}>
              <span style={{ display: 'inline-flex' }}><NamedIcon name="check" size={12} /></span> {b.name}
              <button type="button" onClick={() => removeBrand(b.brand_id, b.name)} disabled={busy}
                aria-label={tr('brandAdd.remove', 'Убрать')} title={tr('brandAdd.remove', 'Убрать')}
                style={{ background: 'none', border: 0, color: 'var(--dim)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 4px' }}>×</button>
            </span>
          ))}
        </div>
      )}

      {added && (
        <div style={{ fontSize: 'var(--fs-small)', color: '#2ecc71' }}>
          {tr('brandAdd.added', '«{{name}}» добавлен.', { name: added })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input
            label={tr('brandAdd.handleLabel', 'Instagram бренда')}
            value={handle}
            onChange={(e) => { setHandle(e.target.value); setResolved(null); setIgCheck(null); setAdded(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') resolveHandle(); }}
            placeholder="@yourbrand"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
        <Button onClick={resolveHandle} loading={busy && !resolved} disabled={handle.trim().length < 2}>
          {tr('brandAdd.find', 'Найти')}
        </Button>
      </div>

      {resolved?.kind === 'brand' && (
        <div style={box('rgba(74,158,255,0.06)', 'rgba(74,158,255,0.25)')}>
          <div style={{ fontSize: 'var(--fs-body)' }}>
            <strong>{resolved.name}</strong>
            <span style={{ color: 'var(--muted)', marginLeft: 6 }}>@{resolved.handle}</span>
            {resolved.followers != null && <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-small)', marginLeft: 6 }}>· {resolved.followers.toLocaleString()} {tr('brandAdd.followers', 'подписчиков')}</span>}
          </div>
          {resolved.claim_state === 'mine' ? (
            <div style={{ fontSize: 'var(--fs-small)', color: '#2ecc71' }}>{tr('brandAdd.alreadyAdded', 'Этот бренд уже в вашем списке.')}</div>
          ) : (
            <>
              {preview && (preview.posts + preview.stories > 0 ? (
                <div style={{ fontSize: 'var(--fs-small)', color: 'var(--text, #dde)', lineHeight: 1.6 }}>
                  {tr('brandAdd.previewFound', 'За 90 дней о бренде написали {{creators}} блогеров: {{posts}} постов и {{stories}} сторис, общая аудитория {{reach}}.', {
                    creators: preview.creators, posts: preview.posts, stories: preview.stories, reach: compactNum(preview.reach) })}
                  {preview.top_creators.length > 0 && (
                    <span style={{ color: 'var(--muted)' }}>{' '}{tr('brandAdd.previewTop', 'Среди них:')}{' '}
                      {preview.top_creators.map((c) => `@${c.handle}`).join(', ')}.</span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 'var(--fs-small)', color: 'var(--muted)', lineHeight: 1.6 }}>
                  {tr('brandAdd.previewNone', 'За 90 дней блогеры о бренде не писали. Мы следим за упоминаниями каждый день и покажем первые, как только они появятся.')}
                </div>
              ))}
              <Button onClick={() => addBrand(resolved.handle)} loading={busy}>{tr('brandAdd.add', 'Добавить')}</Button>
            </>
          )}
        </div>
      )}

      {resolved?.kind === 'unknown' && (
        <div style={box('rgba(255,255,255,0.03)', 'rgba(255,255,255,0.1)')} aria-live="polite">
          {igCheck?.status === 'checking' ? (
            <div style={{ fontSize: 'var(--fs-body)', color: 'var(--muted)' }}>
              {tr('brandAdd.checking', 'Проверяем @{{h}} в Instagram… Это занимает до 30 секунд.', { h: resolved.handle })}
            </div>
          ) : igCheck?.status === 'not_found' ? (
            <div style={{ fontSize: 'var(--fs-body)', color: 'var(--danger-text)', lineHeight: 1.5 }}>
              {tr('brandAdd.notFound', 'Аккаунта @{{h}} в Instagram нет. Проверьте ник — возможно, в нём опечатка.', { h: resolved.handle })}
            </div>
          ) : (
            <>
              {igCheck?.status === 'exists' && (
                <div style={{ fontSize: 'var(--fs-body)' }}>
                  <strong>{igCheck.full_name || `@${resolved.handle}`}</strong>
                  <span style={{ color: 'var(--muted)', marginLeft: 6 }}>@{resolved.handle}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-small)', marginLeft: 6 }}>· {igCheck.followers.toLocaleString()} {tr('brandAdd.followers', 'подписчиков')}</span>
                </div>
              )}
              <div style={{ fontSize: 'var(--fs-body)', color: 'var(--muted)', lineHeight: 1.5 }}>
                {tr('brandAdd.unknown', 'У нас пока нет данных о @{{h}}. Добавьте его — мы начнём собирать упоминания блогеров, первые данные появятся в течение недели.', { h: resolved.handle })}
              </div>
              <Button onClick={() => addBrand(resolved.handle)} loading={busy}>{tr('brandAdd.addHandle', 'Добавить @{{h}}', { h: resolved.handle })}</Button>
            </>
          )}
        </div>
      )}

      {resolved?.kind === 'creator' && (
        <div style={{ ...box('rgba(155,93,229,0.07)', 'rgba(155,93,229,0.3)'), fontSize: 'var(--fs-body)' }}>
          <div>
            {tr('brandAdd.isCreator', 'Похоже, это профиль блогера, а не бренда.')}{' '}
            <button onClick={() => becomeCreator(resolved.handle)} disabled={busy} style={{ background: 'none', border: 0, color: 'var(--accent, #4a9eff)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}>
              {tr('brandAdd.creatorApp', 'Перейти в кабинет блогера')}
            </button>
          </div>
        </div>
      )}

      {err && <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-body)' }}>{err}</div>}
    </div>
  );
}
